import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  Autocomplete
} from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import { FILE_BASE_URL } from '../../../config';
import DocumentInfoAndFiles from '../../common/DocumentInfoAndFiles';
import YKienMauPopup from '../../common/YKienMauPopup';

interface UserData {
  id: number;
  username: string;
  fullname: string;
  position: string;
  department: string;
  email: string;
  roles: string[];
  is_active: boolean;
  created_at: string;
}

interface ChuyenThamMuuPopupProps {
  open: boolean;
  onClose: () => void;
  documentId?: string;
  documentData?: any;
  action?: string; // Action từ menu API
  onActionSuccess?: () => void; // Callback khi action thành công
}

const ChuyenThamMuuPopup: React.FC<ChuyenThamMuuPopupProps> = ({
  open,
  onClose,
  documentId,
  documentData,
  action,
  onActionSuccess
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<UserData | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [yKienMauOpen, setYKienMauOpen] = useState(false);
  const [noteSuggestions, setNoteSuggestions] = useState<string[]>([]);
  const [noteSuggestionsLoading, setNoteSuggestionsLoading] = useState(false);

  // Hàm lấy danh sách note gần đây
  const fetchNoteSuggestions = async () => {
    try {
      setNoteSuggestionsLoading(true);
      const response = await axiosInstance.get('/xlvb/incoming-documents/danhsach-note-top10', {
        params: {
          type: 'incoming_documents.chuyen_thammuu'
        }
      });
      
      if (response.data.code === 0 && response.data.data?.notes) {
        const notes = response.data.data.notes.map((item: any) => item.note);
        setNoteSuggestions(notes);
      } else {
        setNoteSuggestions([]);
      }
    } catch (error) {

      setNoteSuggestions([]);
    } finally {
      setNoteSuggestionsLoading(false);
    }
  };

  // Gọi API lấy danh sách user và note suggestions khi popup mở
  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchNoteSuggestions();
    }
  }, [open]);

  // Hàm lấy danh sách user
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await axiosInstance.get('/qlvb/incoming-documents/users-chuyen-thammuu');
      
      if (response.data.code === 0) {
        const usersList = response.data.data.users || [];
        setUsers(usersList);
        
        // Tự động chọn trưởng phòng hành chính nếu có trong danh sách
        const truongPhongHCTC = usersList.filter((user: UserData) => 
          user.roles && user.roles.includes('TRUONG_PHONG_HCTC')
        );
        
        if (truongPhongHCTC.length > 0) {
          // Nếu có nhiều người với role TRUONG_PHONG_HCTC, chọn người có position "Trưởng Phòng"
          if (truongPhongHCTC.length > 1) {
            const truongPhong = truongPhongHCTC.find((user: UserData) => 
              user.position === 'Trưởng Phòng'
            );
            if (truongPhong) {
              setSelectedPerson(truongPhong);
            } else {
              // Nếu không tìm thấy position "Trưởng Phòng", chọn người đầu tiên
              setSelectedPerson(truongPhongHCTC[0]);
            }
          } else {
            // Chỉ có 1 người với role TRUONG_PHONG_HCTC
            setSelectedPerson(truongPhongHCTC[0]);
          }
        } else if (usersList.length === 1) {
          // Tự động chọn user nếu danh sách chỉ có 1 bản ghi
          setSelectedPerson(usersList[0]);
        }
      } else {
        const errorMsg = response.data.message || 'Có lỗi xảy ra khi tải danh sách người dùng';
        setUsersError(errorMsg);
        enqueueSnackbar(errorMsg, {
          variant: 'error',
          autoHideDuration: 4000
        });
      }
    } catch (error) {

      const errorMsg = 'Không thể tải danh sách người dùng';
      setUsersError(errorMsg);
      enqueueSnackbar(errorMsg, {
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setUsersLoading(false);
    }
  };

  // Hàm xử lý submit
  const handleSubmit = async () => {
    if (!selectedPerson || !documentId) {
      enqueueSnackbar('Vui lòng chọn người chuyển tham mưu', {
        variant: 'warning',
        autoHideDuration: 3000
      });
      return;
    }

    setLoading(true);
    try {       
      // Kiểm tra action có tồn tại không
      if (!action) {
        enqueueSnackbar('Chưa có api', { variant: 'error' });
        return;
      }

      // Gọi API chuyển tham mưu
      const response = await axiosInstance.post(action, {
        doc_id: parseInt(documentId),
        user_tham_muu_id: selectedPerson.id,
        note: note,
        users_lienquan: [],
      });

      if (response.data.code === 0) {
        enqueueSnackbar('Chuyển tham mưu thành công!', {
          variant: 'success',
          autoHideDuration: 3000
        });
        onActionSuccess?.(); // Gọi callback để refresh danh sách và đóng block thông tin
        handleClose();
      } else {
        const errorMsg = response.data.message || 'Có lỗi xảy ra khi chuyển tham mưu';
        enqueueSnackbar(errorMsg, {
          variant: 'error',
          autoHideDuration: 4000
        });
      }
    } catch (error) {

      enqueueSnackbar('Có lỗi xảy ra khi chuyển tham mưu', {
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  // Hàm đóng popup
  const handleClose = () => {
    setSelectedPerson(null);
    setSearchTerm('');
    setNote('');
    setUsers([]);
    setUsersError(null);
    setYKienMauOpen(false);
    onClose();
  };

  // Hàm mở popup ý kiến mẫu
  const handleOpenYKienMau = () => {
    setYKienMauOpen(true);
  };

  // Hàm đóng popup ý kiến mẫu
  const handleCloseYKienMau = () => {
    setYKienMauOpen(false);
  };

  // Hàm xử lý khi chọn template từ popup ý kiến mẫu
  const handleTemplateSelect = (templateContent: string) => {
    setNote(templateContent);
    setYKienMauOpen(false);
  };

  // Hàm format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Filter users
  const filteredUsers = users.filter(user =>
    user.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#086428',
        color: 'white',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '51px',
        padding: '12px 16px',
        boxSizing: 'border-box'
      }}>
        Chuyển tham mưu
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Đang tải danh sách người dùng...</Typography>
          </Box>
        ) : usersError ? (
          <Box sx={{ py: 2 }}>
            <Typography color="error" sx={{ textAlign: 'center' }}>
              {usersError}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', minHeight: '500px' }}>
            {/* Bên trái - DocumentInfoAndFiles + Form chuyển tham mưu */}
            <Box sx={{ 
              flex: 1, 
              pr: 2, 
              borderRight: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {/* DocumentInfoAndFiles - Chỉ hiển thị thông tin văn bản */}
              <Box>
                <DocumentInfoAndFiles 
                  documentData={documentData}
                  showPreview={false}
                  showOnlyInfo={true}
                />
              </Box>

              {/* Form chuyển tham mưu */}
              <Box>
                {/* Chọn người chuyển tham mưu */}
                <Autocomplete
                  options={filteredUsers}
                  getOptionLabel={(option) => `${option.fullname} - ${option.position} (${option.department})`}
                  value={selectedPerson}
                  onChange={(event, newValue) => {
                    setSelectedPerson(newValue);
                  }}
                  inputValue={searchTerm}
                  onInputChange={(event, newInputValue) => {
                    setSearchTerm(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Chọn người chuyển tham mưu"
                      variant="outlined"
                      fullWidth
                      required
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 'auto',
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography>{option.fullname} - {option.position} ({option.department})</Typography>
                    </Box>
                  )}
                  noOptionsText="Không tìm thấy người dùng"
                  clearOnEscape
                  selectOnFocus
                  handleHomeEndKeys
                  sx={{ mb: 2 }}
                />
                
                {/* Ghi chú */}
                <Autocomplete
                  freeSolo
                  options={noteSuggestions}
                  value={note}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setNote(newValue || '');
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setNote(newValue);
                    } else if (newValue === null) {
                      setNote('');
                    }
                  }}
                  loading={noteSuggestionsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ghi chú"
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={5}
                      placeholder="Nhập ghi chú (tùy chọn)..."
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 'auto',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {noteSuggestionsLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option}>
                      <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                    },
                  }}
                />

                {/* Nút Mẫu nhập */}
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleOpenYKienMau}
                    sx={{
                      fontSize: '12px',
                      textTransform: 'none',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: '#f5f5f5'
                      }, 
                      textDecoration: 'underline'
                    }}
                  >
                    (Mẫu nhập)
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Bên phải - Chỉ hiển thị file đính kèm từ DocumentInfoAndFiles */}
            <Box sx={{ flex: 1, pl: 2 }}>
              <DocumentInfoAndFiles 
                documentData={documentData}
                showPreview={true}
                showOnlyFiles={true}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        justifyContent: 'flex-end'
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
        >
          Hủy
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedPerson}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Đang xử lý...' : 'Chuyển tham mưu'}
        </Button>
      </DialogActions>

      {/* Popup Ý kiến mẫu */}
      <YKienMauPopup
        open={yKienMauOpen}
        onClose={handleCloseYKienMau}
        onTemplateSelect={handleTemplateSelect}
      />
    </Dialog>
  );
};

export default ChuyenThamMuuPopup;